import { Stack, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Box } from '@mui/system';
import { LoadingScreen, NextImage } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import { useRouter } from 'next/router';
import { SubscriptionPlan } from '../../../t2c-fe/hooks/subscription-plans/useGetSubscriptionPlans';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlansFAQ } from '../subscription/PlansFAQ';
import PlanCard from '../subscription/PlanCard';
import { PlanType } from '../../@types';
import { useGoogleLogin, usePaymentHandler } from '../../hooks';
import CreditAssignment from '../subscription/CreditAssignmentModal';
import { CustomDialogContainer } from '../../Common/dialog';
import CheckoutPage from '../subscription/CheckoutPage';
import { BillingDetails } from '../subscription/BillingDetails';
import { useGoogleLoginHandler } from '@t2c/auth';
import { useGetActiveSubscriptions } from '../../hooks/subscription-plans/useGetActiveSubscriptions';
import PublicNav from './PublicNav';

interface Props {
  isAlphaPricingMode?: boolean;
}

enum Interval {
  MONTHLY = 'Monthly',
  YEARLY = 'Yearly',
  SPECIAL = 'Special',
  LIFETIME = 'Truly Lifetime',
}

export const ALPHA_PRICING_PLANS_IDS = {
  yearly_trial: 'special_7_days_trial_all_access_yearly',
  monthly_trial: 'special_7_days_trial_all_access_monthly',
};

export const AGENT_PLAN_IDS = {
  monthly: 'agent_plan_monthly',
  yearly: 'agent_plan_yearly',
  monthly_trial: 'special_7_days_trial_agent_monthly',
  yearly_trial: 'special_7_days_trial_agent_yearly',
};

/** All agent plan unique IDs for filtering */
export const ALL_AGENT_PLAN_UNIQUE_IDS = Object.values(AGENT_PLAN_IDS);

/** Check whether a planUniqueId belongs to an agent plan */
export const isAgentPlan = (planUniqueId?: string | null): boolean =>
  !!planUniqueId && ALL_AGENT_PLAN_UNIQUE_IDS.includes(planUniqueId);

/** Check whether a planUniqueId is an agent trial plan */
export const isAgentTrialPlan = (planUniqueId?: string | null): boolean =>
  planUniqueId === AGENT_PLAN_IDS.monthly_trial || planUniqueId === AGENT_PLAN_IDS.yearly_trial;

const LIFETIME_PLAN_UNIQUE_ID = 'lifetime_plan_150m';
const enableAgentPlan = true;

export function getAlphaPricingPlanUniqueIdFromQuery(
  alphaPlanType?: 'yearly_trial' | 'monthly_trial'
): string {
  // If router query isn't hydrated yet, don't default to yearly —
  // return empty string so the payment handler isn't seeded prematurely.
  if (!alphaPlanType) return '';
  if (alphaPlanType === 'monthly_trial') return ALPHA_PRICING_PLANS_IDS.monthly_trial;
  return ALPHA_PRICING_PLANS_IDS.yearly_trial;
}

const PricingPage: React.FC<Props> = ({ isAlphaPricingMode }) => {
  const { push } = useRouter();
  const [subscriptionPlanData, setSubscriptionPlanData] = useState<SubscriptionPlan[]>([]);
  const handleGetStarted = () => push('/');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isSmallScreen = isMobile || isTablet;

  const enableLifetimeInAlphaPricing = false;
  // Controls lifetime visibility for normal (non-alpha) pricing page.
  // Default is false.
  const enableLifetimeInNormalPricing = false;

  const t2cUrl = 'https://txt2create.com';
  const { query } = useRouter();
  const [intervalTab, setIntervalTab] = useState<Interval>(() => {
    if (isAlphaPricingMode) return Interval.MONTHLY;
    return enableLifetimeInNormalPricing ? Interval.LIFETIME : Interval.YEARLY;
  });

  useEffect(() => {
    if (
      !isAlphaPricingMode &&
      !enableLifetimeInNormalPricing &&
      intervalTab === Interval.LIFETIME
    ) {
      setIntervalTab(Interval.YEARLY);
    }
  }, [isAlphaPricingMode, enableLifetimeInNormalPricing, intervalTab]);

  const {
    handleGoogleLoginSuccess,
    handleOnError,
    isLoading: isLogging,
    isLoggedIn,
  } = useGoogleLogin({ shouldRedirectOnSuccess: false });
  const { handleLogin } = useGoogleLoginHandler({
    onSuccess: handleGoogleLoginSuccess,
    onError: handleOnError,
  });

  const alphaFilter = useMemo(
    () =>
      [
        ALPHA_PRICING_PLANS_IDS.monthly_trial,
        ALPHA_PRICING_PLANS_IDS.yearly_trial,
        ...(enableAgentPlan ? [AGENT_PLAN_IDS.monthly_trial, AGENT_PLAN_IDS.yearly_trial] : []),
        ...(enableLifetimeInAlphaPricing ? [LIFETIME_PLAN_UNIQUE_ID] : []),
      ]
        .filter(Boolean)
        .join(','),
    [enableLifetimeInAlphaPricing]
  );

  const {
    isOpen,
    setIsOpen,
    isExtraSmall,
    options,
    isAssigningCredit,
    selectedPlan,
    setSelectedPlan,
    showCheckoutOption,
    subscriptionPlans,
    isLoading,
    createPaymentMutationIsLoading,
    handleBuy,
    handleSuccess,
    handleCancel,
    handleClose,
  } = usePaymentHandler(isAlphaPricingMode ? alphaFilter : '');

  const [currentPlanIds, setCurrentPlanIds] = useState<string[]>([]);
  const [purchasedPlanIds, setPurchasedPlanIds] = useState<string[]>([]);
  const [hasPurchasedAgentPlan, setHasPurchasedAgentPlan] = useState(false);
  const [activePlansFetching, setActivePlansFetching] = useState(false);
  const [isTrialPlanPurchasable, setIsTrialPlanPurchasable] = useState(true);
  const [activeTrialPlanUniqueId, setActiveTrialPlanUniqueId] = useState<string | null>(null);
  const [activeTrialPlanUniqueIds, setActiveTrialPlanUniqueIds] = useState<string[]>([]);
  const { refetch } = useGetActiveSubscriptions(false);

  // Column layout when multiple plan types (All Access + Agent) are visible; row when single plan.
  // Agent plans should only appear after an agent purchase, including on alpha-pricing.
  const showMultiplePlans = intervalTab !== Interval.LIFETIME && hasPurchasedAgentPlan;

  useEffect(() => {
    if (isLoggedIn) {
      setActivePlansFetching(true);
      refetch().then((res) => {
        if (res.data) {
          setPurchasedPlanIds(res.data.map((sub) => sub.planId));
          setHasPurchasedAgentPlan(res.data.some((sub) => isAgentPlan(sub.planUniqueId)));
          setCurrentPlanIds(res.data.filter((sub) => sub.isActive).map((sub) => sub.planId));
          const activeTrialPlanUniqueIds = (res.data ?? [])
            .filter(
              (sub) =>
                sub.isActive &&
                sub.interval === Interval.SPECIAL &&
                (sub.planUniqueId === 'special_7_days_trial_all_access_yearly' ||
                  sub.planUniqueId === 'special_7_days_trial_all_access_monthly' ||
                  sub.planUniqueId === 'lifetime_plan_150m' ||
                  isAgentTrialPlan(sub.planUniqueId))
            )
            .map((sub) => sub.planUniqueId)
            .filter(Boolean) as string[];
          const trialPlanDetails = (res.data ?? []).find(
            (sub) =>
              sub.interval === Interval.SPECIAL &&
              (sub.planUniqueId === 'special_7_days_trial_all_access_yearly' ||
                sub.planUniqueId === 'special_7_days_trial_all_access_monthly' ||
                sub.planUniqueId === 'lifetime_plan_150m' ||
                isAgentTrialPlan(sub.planUniqueId))
          );
          if (res.data.length > 0) {
            setIsTrialPlanPurchasable(false);
          } else {
            setIsTrialPlanPurchasable(true);
          }
          setActiveTrialPlanUniqueIds(activeTrialPlanUniqueIds);
          if (trialPlanDetails?.isActive) {
            setActiveTrialPlanUniqueId(trialPlanDetails.planUniqueId);
          } else {
            setActiveTrialPlanUniqueId(null);
          }
        }
      });
      setActivePlansFetching(false);
    } else {
      setCurrentPlanIds([]);
      setPurchasedPlanIds([]);
      setHasPurchasedAgentPlan(false);
      setActiveTrialPlanUniqueId(null);
      setActiveTrialPlanUniqueIds([]);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (subscriptionPlans?.length) {
      if (isAlphaPricingMode) {
        const updatedData = subscriptionPlans.filter((item) => {
          if (item.interval !== Interval.SPECIAL) return false;
          if (enableLifetimeInAlphaPricing) return true;
          return item.planUniqueId !== LIFETIME_PLAN_UNIQUE_ID;
        });
        setSubscriptionPlanData(updatedData);
      } else {
        setSubscriptionPlanData(subscriptionPlans);
      }
    }
  }, [subscriptionPlans, isAlphaPricingMode, enableLifetimeInAlphaPricing]);

  const alphaSelectedPlanUniqueId = useMemo(() => {
    if (!isAlphaPricingMode) return '';
    if (intervalTab === Interval.LIFETIME)
      return enableLifetimeInAlphaPricing ? LIFETIME_PLAN_UNIQUE_ID : '';
    if (intervalTab === Interval.MONTHLY) return ALPHA_PRICING_PLANS_IDS.monthly_trial;
    return ALPHA_PRICING_PLANS_IDS.yearly_trial;
  }, [isAlphaPricingMode, intervalTab, enableLifetimeInAlphaPricing]);

  const isTrialPlanUniqueId = useCallback((planUniqueId?: string | null) => {
    return (
      planUniqueId === 'special_7_days_trial_all_access_yearly' ||
      planUniqueId === 'special_7_days_trial_all_access_monthly'
    );
  }, []);

  // After redirect from auth/callback to a pricing page, restore pending plan & automatically open billing dialog.
  // If showCheckoutOption toggles later (after initiating payment), flow will progress naturally.
  useEffect(() => {
    if (!isLoggedIn || isOpen || selectedPlan) return;
    let restored = false;
    try {
      const raw = window.sessionStorage.getItem('post_login_plan');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.id && Date.now() - (parsed.ts || 0) < 10 * 60 * 1000) {
          const plan = subscriptionPlanData.find((p) => p.id === parsed.id);
          if (plan) {
            if (currentPlanIds.includes(plan.id)) {
              // User is already subscribed to this plan, don't open billing dialog
              restored = true;
            } else if (
              isTrialPlanUniqueId(plan.planUniqueId) &&
              (!isTrialPlanPurchasable || !!activeTrialPlanUniqueId)
            ) {
              // Trial plan not purchasable or already active, don't open billing dialog
              restored = true;
            } else {
              setSelectedPlan({
                id: plan.id,
                amount: plan.amount,
                discount: plan.discountPercent,
                interval: plan.interval,
                name: plan.name,
              });
              setIsOpen(true); // auto-open billing dialog, user does not need to click Subscribe again
              restored = true;
            }
          }
        }
      }
    } catch {
    } finally {
      if (restored) {
        try {
          window.sessionStorage.removeItem('post_login_plan');
        } catch {}
        try {
          window.sessionStorage.removeItem('post_login_redirect');
        } catch {}
      }
    }
  }, [
    isLoggedIn,
    isOpen,
    selectedPlan,
    subscriptionPlanData,
    setSelectedPlan,
    setIsOpen,
    currentPlanIds,
    isTrialPlanPurchasable,
    activeTrialPlanUniqueId,
  ]);

  const renderIntervalToggle = (isMobileVariant = false) => {
    const containerSx = { ...styles.toggleContainer, ...(isMobileVariant ? { mt: 3 } : {}) };

    if (isAlphaPricingMode) {
      return (
        <Box sx={containerSx}>
          <Box
            sx={intervalTab === Interval.MONTHLY ? styles.activeTab : styles.tab}
            onClick={() => setIntervalTab(Interval.MONTHLY)}
          >
            Monthly
          </Box>
          <Box
            sx={intervalTab === Interval.YEARLY ? styles.activeTab : styles.tab}
            onClick={() => setIntervalTab(Interval.YEARLY)}
          >
            Annual
            <Box sx={styles.discountBadge}>
              <Typography sx={styles.discountText}>
                {isMobileVariant ? 'Save up to 91%' : 'Save up to 46%'}
              </Typography>
            </Box>
          </Box>
          {enableLifetimeInAlphaPricing && (
            <Box
              sx={intervalTab === Interval.LIFETIME ? styles.activeTabLifetime : styles.tabLifetime}
              onClick={() => setIntervalTab(Interval.LIFETIME)}
            >
              Truly Lifetime
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Box sx={containerSx}>
        <Box
          sx={intervalTab === Interval.MONTHLY ? styles.activeTab : styles.tab}
          onClick={() => setIntervalTab(Interval.MONTHLY)}
        >
          Monthly
        </Box>
        <Box
          sx={intervalTab === Interval.YEARLY ? styles.activeTab : styles.tab}
          onClick={() => setIntervalTab(Interval.YEARLY)}
        >
          Annual
          <Box sx={styles.discountBadge}>
            <Typography sx={styles.discountText}>
              {isMobileVariant ? 'Save up to 91%' : 'Save up to 46%'}
            </Typography>
          </Box>
        </Box>
        {enableLifetimeInNormalPricing && (
          <Box
            sx={intervalTab === Interval.LIFETIME ? styles.activeTabLifetime : styles.tabLifetime}
            onClick={() => setIntervalTab(Interval.LIFETIME)}
          >
            Truly Lifetime
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          ...styles.container,
          px: isSmallScreen ? 2 : 5,
          backgroundColor: '#16171A', // Dark background from design
        }}
      >
        <Box>
          <PublicNav active="Pricing" />
          {isLoading ? (
            <Stack sx={styles.loaderContainer}>
              <LoadingScreen />
            </Stack>
          ) : (
            <Box
              sx={{
                ...styles.mainWrapper,
                width: isSmallScreen ? '100%' : '70%',
                padding: isSmallScreen ? '24px 0 0' : '24px',
                borderRadius: isSmallScreen ? '16px' : '48px',
              }}
            >
              {/* Background Blurs */}
              <Box sx={styles.blurEllipse1} />
              <Box sx={styles.blurRect1} />

              <Box sx={styles.contentContainer}>
                {/* Top Bar */}
                <Box
                  sx={{
                    ...styles.topBar,
                    justifyContent: isSmallScreen ? 'center' : 'flex-start',
                    flexDirection: isSmallScreen ? 'column' : 'row',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ ...styles.banner }}>
                    <Typography sx={styles.bannerText}>
                      Switch from $650+/month to one simple price!
                    </Typography>
                  </Box>

                  {!isMobile && renderIntervalToggle(false)}
                </Box>

                {/* Main Content Row */}
                <Box
                  sx={{
                    ...styles.mainContentRow,
                    flexDirection: showMultiplePlans || isSmallScreen ? 'column' : 'row',
                    justifyContent: isSmallScreen ? 'center' : 'space-between',
                  }}
                >
                  {/* Left Side Text */}
                  <Box
                    sx={{
                      ...styles.leftTextContainer,
                      alignItems: showMultiplePlans || isSmallScreen ? 'center' : 'flex-start',
                      maxWidth: showMultiplePlans || isSmallScreen ? '100%' : '30%',
                      marginTop: isSmallScreen ? '0' : '5%',
                      marginBottom: showMultiplePlans || isSmallScreen ? '5%' : '0',
                    }}
                  >
                    <Typography
                      sx={{ ...styles.heading, textAlign: isSmallScreen ? 'center' : 'left' }}
                    >
                      Simple, Transparent, Affordable
                    </Typography>
                    <Typography
                      sx={{ ...styles.subHeading, textAlign: isSmallScreen ? 'center' : 'left' }}
                    >
                      The perfect plan for your needs
                    </Typography>
                    {isMobile && renderIntervalToggle(true)}
                  </Box>

                  {/* Right Side Plan Card */}
                  <Box
                    sx={{
                      ...styles.rightCardContainer,
                      justifyContent: isSmallScreen ? 'center' : 'flex-end',
                      minWidth: isSmallScreen ? 'auto' : '360px',
                      flexDirection: isSmallScreen ? 'column-reverse' : 'row',
                      gap: isSmallScreen ? 0 : 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    {(subscriptionPlanData ?? [])
                      .filter((res) => {
                        if (
                          isAlphaPricingMode &&
                          !hasPurchasedAgentPlan &&
                          isAgentPlan(res?.planUniqueId)
                        ) {
                          return false;
                        }

                        if (showMultiplePlans && isAgentPlan(res?.planUniqueId)) return false;

                        if (!isAlphaPricingMode) {
                          if (intervalTab === Interval.LIFETIME)
                            return res?.planUniqueId === LIFETIME_PLAN_UNIQUE_ID;
                          return res?.interval === intervalTab && !isAgentPlan(res?.planUniqueId);
                        }

                        if (!alphaSelectedPlanUniqueId) {
                          // alpha mode shows only Special plans (trial + optionally lifetime)
                          return res?.interval === Interval.SPECIAL;
                        }

                        const matchesExact =
                          res?.planUniqueId === alphaSelectedPlanUniqueId ||
                          res?.id === alphaSelectedPlanUniqueId;
                        const matchesByLabel =
                          intervalTab === Interval.MONTHLY
                            ? res?.planUniqueId?.toLowerCase().includes('month') ||
                              res?.name?.toLowerCase().includes('month')
                            : intervalTab === Interval.LIFETIME
                            ? res?.planUniqueId === LIFETIME_PLAN_UNIQUE_ID
                            : res?.planUniqueId?.toLowerCase().includes('year') ||
                              res?.name?.toLowerCase().includes('year');
                        return matchesExact || matchesByLabel;
                      })
                      .map((data, index: number) => {
                        if (data?.isActive === false || data?.name.includes('Addon')) {
                          return null;
                        }
                        const isLifetimePlan = data?.planUniqueId === LIFETIME_PLAN_UNIQUE_ID;
                        const isMostPopular = isLifetimePlan
                          ? true
                          : isAlphaPricingMode
                          ? data?.planUniqueId === ALPHA_PRICING_PLANS_IDS.yearly_trial
                          : false;

                        return (
                          <Box
                            key={data.id}
                            sx={{
                              width: showMultiplePlans && !isSmallScreen ? '48%' : '100%',
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <PlanCard
                              data={data}
                              handleSubmit={() => {
                                if (!createPaymentMutationIsLoading) {
                                  setSelectedPlan({
                                    id: data.id,
                                    amount: data.amount,
                                    discount: data.discountPercent,
                                    interval: data.interval,
                                      name: data.name,
                                  });
                                  setIsOpen(true);
                                }
                              }}
                              isPublic={true}
                              isLoading={activePlansFetching}
                              currentPlanIds={currentPlanIds}
                              isMostPopular={isMostPopular}
                              showCrossIcon={false}
                              isAlphaPricingUniqueId={
                                isAlphaPricingMode ? data?.planUniqueId : undefined
                              }
                              activeTrialPlanUniqueId={activeTrialPlanUniqueId}
                              activeTrialPlanUniqueIds={activeTrialPlanUniqueIds}
                              isTrialPlanPurchasable={isTrialPlanPurchasable}
                              enableAgentPlan={showMultiplePlans}
                            />
                          </Box>
                        );
                      })}

                    {/* Agent plan cards */}
                    {showMultiplePlans &&
                      (subscriptionPlanData ?? [])
                        .filter((res) => {
                          if (isAlphaPricingMode) {
                            if (!isAgentTrialPlan(res?.planUniqueId)) return false;
                            return intervalTab === Interval.MONTHLY
                              ? res?.planUniqueId === AGENT_PLAN_IDS.monthly_trial
                              : res?.planUniqueId === AGENT_PLAN_IDS.yearly_trial;
                          }

                          return (
                            res?.interval === intervalTab &&
                            isAgentPlan(res?.planUniqueId) &&
                            !isAgentTrialPlan(res?.planUniqueId) &&
                            hasPurchasedAgentPlan
                          );
                        })
                        .map((subscriptionPlan) => {
                          if (subscriptionPlan.isActive === false) return null;
                          return (
                            <Box
                              key={subscriptionPlan.id}
                              sx={{
                                width: isSmallScreen ? '100%' : '48%',
                                display: 'flex',
                                justifyContent: 'center',
                              }}
                            >
                              <PlanCard
                                data={subscriptionPlan}
                                handleSubmit={() => {
                                  if (!createPaymentMutationIsLoading) {
                                    setSelectedPlan({
                                      id: subscriptionPlan.id,
                                      amount: subscriptionPlan.amount,
                                      discount: subscriptionPlan.discountPercent,
                                      interval: subscriptionPlan.interval,
                                      name: subscriptionPlan.name,
                                    });
                                    setIsOpen(true);
                                  }
                                }}
                                isPublic={true}
                                isLoading={activePlansFetching}
                                currentPlanIds={currentPlanIds}
                                isMostPopular={false}
                                showCrossIcon={false}
                                activeTrialPlanUniqueId={activeTrialPlanUniqueId}
                                activeTrialPlanUniqueIds={activeTrialPlanUniqueIds}
                                isTrialPlanPurchasable={isTrialPlanPurchasable}
                                enableAgentPlan={enableAgentPlan}
                                isAlphaPricingUniqueId={
                                  isAlphaPricingMode ? subscriptionPlan?.planUniqueId : undefined
                                }
                              />
                            </Box>
                          );
                        })}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        <Box mt={8} maxWidth={isSmallScreen ? '95%' : '75%'} alignSelf={'center'}>
          <PlansFAQ />
        </Box>
      </Box>
      {isAssigningCredit && (
        <CreditAssignment
          paymentId={options?.paymentId || ''}
          handleClose={handleClose}
          isOpen={isAssigningCredit}
        />
      )}
      <CustomDialogContainer open={isOpen} maxWidth="sm" sx={{ zIndex: 999 }}>
        {showCheckoutOption ? (
          <CheckoutPage
            amount={selectedPlan?.amount}
            options={options}
            interval={selectedPlan?.interval}
            discount={selectedPlan?.discount}
            name={selectedPlan?.name}
            handleCancel={handleCancel}
            handleSuccess={handleSuccess}
          />
        ) : (
          <BillingDetails handleOnCancel={handleCancel} handleInitiatePurchase={handleBuy} />
        )}
      </CustomDialogContainer>
    </>
  );
};

const styles: Styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  loaderContainer: {
    minHeight: 350,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mainWrapper: {
    position: 'relative',
    width: '70%',
    margin: '0 auto',
    marginTop: 0,
    backgroundColor: 'rgba(46, 46, 53, 0.5)',
    boxShadow: 'inset -2px -1px 1px 0px #f9f9f96e, inset 1px 1px 1px 0px #f9f9f994',
    overflow: 'hidden',
    minHeight: '640px',
  },
  blurEllipse1: {
    position: 'absolute',
    width: '619px',
    height: '619px',
    left: '306px',
    top: '368px',
    background: '#282C36',
    filter: 'blur(187px)',
    zIndex: 0,
    pointerEvents: 'none',
  },
  blurEllipse2: {
    position: 'absolute',
    width: '213px',
    height: '213px',
    left: '509px',
    top: '770px',
    background: '#FFFFFF',
    filter: 'blur(102px)',
    zIndex: 0,
    pointerEvents: 'none',
  },
  blurRect1: {
    position: 'absolute',
    width: '622px',
    height: '553px',
    right: '-31px',
    top: '158px',
    background: '#282C36',
    opacity: 0.5,
    filter: 'blur(21px)',
    zIndex: 0,
    pointerEvents: 'none',
  },
  blurRect2: {
    position: 'absolute',
    width: '264px',
    height: '235px',
    right: '-26px',
    top: '472px',
    background: '#FFFFFF',
    opacity: 0.5,
    filter: 'blur(21px)',
    zIndex: 0,
    pointerEvents: 'none',
  },
  contentContainer: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '28px',
    flexWrap: 'wrap',
    gap: '12%',
    flexDirection: 'row',
  },
  banner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '8px 16px',
    background: 'rgba(123, 53, 239, 0.2)',
    boxShadow: 'inset -2px -1px 1px 0px #f9f9f96e, inset 1px 1px 1px 0px #f9f9f994',
    borderRadius: '30px',
    marginBottom: '8px',
  },
  bannerText: {
    fontWeight: 500,
    fontSize: 'clamp(12px, 3vw, 14px)',
    color: '#FFFFFF',
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    // background: 'rgba(255, 255, 255, 0.1)',
    // boxShadow: 'inset -2px -1px 1px 0px #f9f9f96e, inset 1px 1px 1px 0px #f9f9f994',
    // borderRadius: '50px',
    padding: '8px',
    gap: '6px',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 400,
    fontSize: 'clamp(14px, 4vw, 18px)',
    gap: '10px',
    color: '#FFFFFF',
    cursor: 'pointer',
    borderRadius: '50px',
    boxShadow: 'inset -2px -1px 1px 0px #7B35EF, inset 2px 1px 1px 0px #7B35EF',
    padding: '8px 16px',
  },
  tabLifetime: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 400,
    fontSize: 'clamp(14px, 4vw, 18px)',
    gap: '10px',
    color: '#D97706',
    background: '#FFFBEB',
    cursor: 'pointer',
    borderRadius: '50px',
    boxShadow: 'inset -2px -1px 1px 0px #EE9B0A, inset 2px 1px 1px 0px #EE9B0A',
    padding: '8px 16px',
  },
  activeTabLifetime: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    fontSize: 'clamp(14px, 4vw, 18px)',
    gap: '10px',
    color: '#FFFBEB',
    background: '#D97706',
    cursor: 'pointer',
    borderRadius: '50px',
    boxShadow: 'inset -2px -1px 1px 0px #EE9B0A, inset 2px 1px 1px 0px #EE9B0A',
    padding: '8px 16px',
  },
  activeTab: {
    display: 'flex',
    alignItems: 'center',
    background: '#7B35EF',
    borderRadius: '30px',
    padding: '8px 16px',
    gap: '10px',
    cursor: 'pointer',
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: 'clamp(14px, 4vw, 18px)',
  },
  discountBadge: {
    background: 'linear-gradient(82.97deg, #000000 7.07%, #666666 136.49%)',
    boxShadow: '0px 0px 10px rgba(0, 187, 255, 0.75)',
    border: '2px solid rgba(0, 149, 255, 0.75)',
    borderRadius: '20px',
    padding: '4px 11px',
  },
  discountText: {
    fontWeight: 600,
    fontSize: 'clamp(10px, 2.5vw, 12px)',
    color: '#FFFFFF',
  },
  mainContentRow: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    flexDirection: 'row',
  },
  leftTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    maxWidth: '420px',
    marginTop: '5%',
  },
  heading: {
    fontWeight: 500,
    fontSize: 'clamp(24px, 5vw, 40px)',
    lineHeight: '140%',
    color: '#FFFFFF',
    marginBottom: '8px',
    textAlign: 'left',
  },
  subHeading: {
    fontWeight: 400,
    fontSize: 'clamp(18px, 3vw, 25px)',
    lineHeight: '140%',
    color: '#FFFFFF',
    textAlign: 'left',
  },
  rightCardContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    minWidth: '360px',
  },
};

// Export styles for reuse by other pages (express checkout etc.)
export const pricingPageStyles = styles;

export default PricingPage;
